
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Alert } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';

export const useAlertsRealtime = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const mountedRef = useRef(true);
  const channelRef = useRef<any>(null);
  const readAlertsRef = useRef<Set<string>>(new Set());

  // Initialize read alerts from localStorage
  useEffect(() => {
    try {
      const savedReadAlerts = localStorage.getItem('readAlerts');
      if (savedReadAlerts) {
        readAlertsRef.current = new Set(JSON.parse(savedReadAlerts));
      }
    } catch (error) {
      console.error('[useAlertsRealtime] Error loading read alerts from localStorage:', error);
    }
  }, []);

  // Generate alerts from properties
  const generateAlertsFromProperties = useCallback(async () => {
    if (!mountedRef.current) return [];
    
    try {
      console.log('[useAlertsRealtime] Generating alerts from properties...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('[useAlertsRealtime] No authenticated user found');
        return [];
      }

      // Fetch properties with details needed for alerts
      const { data: properties, error } = await supabase
        .from('property_details')
        .select('*, properties(id, address, tags)')
        .eq('user_id', user.id);

      if (error) {
        console.error('[useAlertsRealtime] Error fetching properties:', error);
        return [];
      }

      const generatedAlerts: Alert[] = [];
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      properties?.forEach(property => {
        const propertyAddress = property.properties?.address || property.address || 'Unknown property';
        const propertyId = property.properties?.id || property.id;

        // Only generate alerts if we have valid data and dates are meaningful
        // Insurance expiry alerts - only if date is set and not default
        if (property.insurance_expiry_date && property.insurance_expiry_date !== '2025-05-23') {
          const expiryDate = new Date(property.insurance_expiry_date);
          // Only create alert if expiry date is realistic (not in the past more than a year or too far in future)
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(today.getFullYear() - 1);
          const twoYearsFromNow = new Date();
          twoYearsFromNow.setFullYear(today.getFullYear() + 2);
          
          if (expiryDate > oneYearAgo && expiryDate < twoYearsFromNow && expiryDate <= thirtyDaysFromNow) {
            const alertId = `insurance-${propertyId}`;
            generatedAlerts.push({
              id: alertId,
              type: expiryDate < today ? 'critical' : 'warning',
              message: `Insurance for ${propertyAddress} expires on ${expiryDate.toLocaleDateString()}`,
              propertyId: propertyId,
              dueDate: property.insurance_expiry_date,
              isRead: readAlertsRef.current.has(alertId),
              category: 'insurance'
            });
          }
        }

        // Boiler service alerts - only if date is set and not default
        if (property.boiler_next_service_date && property.boiler_next_service_date !== '2025-05-23') {
          const serviceDate = new Date(property.boiler_next_service_date);
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(today.getFullYear() - 1);
          const twoYearsFromNow = new Date();
          twoYearsFromNow.setFullYear(today.getFullYear() + 2);
          
          if (serviceDate > oneYearAgo && serviceDate < twoYearsFromNow && serviceDate <= thirtyDaysFromNow) {
            const alertId = `boiler-${propertyId}`;
            generatedAlerts.push({
              id: alertId,
              type: serviceDate < today ? 'critical' : 'warning',
              message: `Boiler service for ${propertyAddress} is due on ${serviceDate.toLocaleDateString()}`,
              propertyId: propertyId,
              dueDate: property.boiler_next_service_date,
              isRead: readAlertsRef.current.has(alertId),
              category: 'maintenance'
            });
          }
        }

        // EPC expiry alerts - only if date is set and not default
        if (property.epc_expiry_date && property.epc_expiry_date !== '2025-05-23') {
          const epcDate = new Date(property.epc_expiry_date);
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(today.getFullYear() - 1);
          const twoYearsFromNow = new Date();
          twoYearsFromNow.setFullYear(today.getFullYear() + 2);
          
          if (epcDate > oneYearAgo && epcDate < twoYearsFromNow && epcDate <= thirtyDaysFromNow) {
            const alertId = `epc-${propertyId}`;
            generatedAlerts.push({
              id: alertId,
              type: epcDate < today ? 'critical' : 'warning',
              message: `EPC certificate for ${propertyAddress} expires on ${epcDate.toLocaleDateString()}`,
              propertyId: propertyId,
              dueDate: property.epc_expiry_date,
              isRead: readAlertsRef.current.has(alertId),
              category: 'legal'
            });
          }
        }
        
        // Mortgage renewal alerts - only if date is set and meaningful
        if (property.mortgage_renewal_date) {
          const renewalDate = new Date(property.mortgage_renewal_date);
          const sixtyDaysFromNow = new Date();
          sixtyDaysFromNow.setDate(today.getDate() + 60);
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(today.getFullYear() - 1);
          const fiveYearsFromNow = new Date();
          fiveYearsFromNow.setFullYear(today.getFullYear() + 5);
          
          if (renewalDate > oneYearAgo && renewalDate < fiveYearsFromNow && renewalDate <= sixtyDaysFromNow) {
            const alertId = `mortgage-${propertyId}`;
            generatedAlerts.push({
              id: alertId,
              type: renewalDate <= thirtyDaysFromNow ? 'warning' : 'info',
              message: `Mortgage renewal for ${propertyAddress} is due on ${renewalDate.toLocaleDateString()}`,
              propertyId: propertyId,
              dueDate: property.mortgage_renewal_date,
              isRead: readAlertsRef.current.has(alertId),
              category: 'mortgage'
            });
          }
        }
      });

      console.log(`[useAlertsRealtime] Generated ${generatedAlerts.length} alerts from properties`);
      return generatedAlerts;
    } catch (error) {
      console.error('[useAlertsRealtime] Error generating alerts:', error);
      return [];
    }
  }, []);

  // Fetch tasks and convert to alerts
  const generateAlertsFromTasks = useCallback(async () => {
    if (!mountedRef.current) return [];
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      // For now - just return empty array as tasks aren't implemented in the DB yet
      // When a tasks table is added, we can fetch and convert tasks to alerts here
      return [];
    } catch (error) {
      console.error('[useAlertsRealtime] Error generating task alerts:', error);
      return [];
    }
  }, []);

  // Combined fetch function for all alert types
  const fetchAlerts = useCallback(async () => {
    if (!mountedRef.current) return;
    
    try {
      setLoading(true);
      console.log('[useAlertsRealtime] Fetching alerts...');
      
      // Get alerts from different sources
      const [propertyAlerts, taskAlerts] = await Promise.all([
        generateAlertsFromProperties(),
        generateAlertsFromTasks()
      ]);
      
      // Combine all alerts
      const combinedAlerts = [...propertyAlerts, ...taskAlerts];
      
      // Sort alerts by priority (critical first, then warning, then info)
      // and then by due date (most urgent first)
      const sortedAlerts = combinedAlerts.sort((a, b) => {
        const priorityOrder = { critical: 0, warning: 1, info: 2 };
        const priorityDiff = 
          priorityOrder[a.type as keyof typeof priorityOrder] - 
          priorityOrder[b.type as keyof typeof priorityOrder];
        
        if (priorityDiff !== 0) return priorityDiff;
        
        // Date sort if same priority
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      });

      if (mountedRef.current) {
        console.log(`[useAlertsRealtime] Setting ${sortedAlerts.length} alerts in state`);
        setAlerts(sortedAlerts);
      }
    } catch (error) {
      console.error('[useAlertsRealtime] Error fetching alerts:', error);
      if (mountedRef.current) {
        toast({
          title: "Error",
          description: "Failed to load alerts from database",
          variant: "destructive",
        });
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [generateAlertsFromProperties, generateAlertsFromTasks, toast]);

  // Function to mark an alert as read
  const markAlertAsRead = useCallback((alertId: string) => {
    if (!alertId) return;

    // Add to read alerts set
    readAlertsRef.current.add(alertId);
    
    // Save to localStorage
    try {
      localStorage.setItem('readAlerts', JSON.stringify([...readAlertsRef.current]));
    } catch (error) {
      console.error('[useAlertsRealtime] Error saving read alerts to localStorage:', error);
    }
    
    // Update alerts state
    setAlerts(currentAlerts => 
      currentAlerts.map(alert => 
        alert.id === alertId ? { ...alert, isRead: true } : alert
      )
    );
  }, []);

  useEffect(() => {
    console.log('[useAlertsRealtime] Component mounting, setting up...');
    mountedRef.current = true;

    // Initial fetch
    fetchAlerts();

    // Set up subscription
    const setupSubscription = () => {
      try {
        const channelName = `alerts-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
        console.log(`[useAlertsRealtime] Setting up subscription with channel: ${channelName}`);
        
        const channel = supabase
          .channel(channelName)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'property_details'
            },
            (payload) => {
              if (!mountedRef.current) return;
              console.log('[useAlertsRealtime] Property details changed:', payload);
              console.log('[useAlertsRealtime] Refetching alerts due to property change...');
              fetchAlerts();
            }
          )
          .subscribe((status) => {
            console.log('[useAlertsRealtime] Subscription status:', status);
            if (status === 'SUBSCRIBED') {
              console.log('[useAlertsRealtime] Successfully subscribed to real-time updates');
            } else if (status === 'CHANNEL_ERROR') {
              console.error('[useAlertsRealtime] Subscription error - will retry fetch');
              setTimeout(() => {
                if (mountedRef.current) {
                  fetchAlerts();
                }
              }, 1000);
            }
          });
          
        channelRef.current = channel;
      } catch (error) {
        console.error('[useAlertsRealtime] Error setting up subscription:', error);
      }
    };

    setupSubscription();

    return () => {
      console.log('[useAlertsRealtime] Component unmounting, cleaning up...');
      mountedRef.current = false;
      
      if (channelRef.current) {
        try {
          console.log('[useAlertsRealtime] Removing channel subscription');
          supabase.removeChannel(channelRef.current);
        } catch (error) {
          console.error('[useAlertsRealtime] Error removing subscription:', error);
        }
      }
    };
  }, [fetchAlerts]);

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;
    console.log('[useAlertsRealtime] Manual refetch requested');
    await fetchAlerts();
  }, [fetchAlerts]);

  return {
    alerts,
    loading,
    refetch,
    markAlertAsRead
  };
};
